"use client";

import { LanguageParams } from "@/lib/types/PageProps";
import { RefObject, useEffect, useMemo, useState } from "react";
import Marker from "./Marker";
import MarkerDetails from "./MarkerDetails";
import { RoomProps } from "../Room";

type Props = {
    listings: RoomProps[];
    onListingClick: (listing: RoomProps) => void;
    container: RefObject<HTMLDivElement>;
    selectedRoom: RoomProps | null;
} & LanguageParams;

const Markers = ({ listings, onListingClick, container, lang, selectedRoom }: Props) => {
    const [openedListing, setOpenedListing] = useState<RoomProps | null>(null); // which Room widget is displayed

    const openListing = (listing: RoomProps) => {
        return () => {
            setOpenedListing(listing);
            onListingClick(listing);
        };
    };

    const getRoomKey = (room: RoomProps) => `${room.location.lat}-${room.location.lng}`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const groupedListings = useMemo(() => {
        const groupedListings: Record<string, RoomProps> = {};
        for (const listing in listings) {
            const el = listings[listing];
            const key = getRoomKey(el);
            if (!groupedListings[key] || groupedListings[key].pricePerMonth > el.pricePerMonth) {
                groupedListings[key] = el;
            }
        }
        return groupedListings;
    }, [listings]);

    // this closes room widget if selected marker changed - usually in case of hover
    useEffect(() => {
        if (selectedRoom?.roomId !== openedListing?.roomId) {
            setOpenedListing(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoom]);

    const markers = useMemo(
        () =>
            Object.entries(groupedListings).map(([key, l]) => (
                <Marker
                    lang={lang}
                    key={key}
                    listing={l}
                    onOpen={openListing(l)}
                    isSelected={selectedRoom ? getRoomKey(selectedRoom) === getRoomKey(l) : false}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [groupedListings, selectedRoom],
    );
    const markerDetails = useMemo(
        () =>
            openedListing ? (
                <MarkerDetails
                    listing={openedListing}
                    lang={lang}
                    onClose={() => setOpenedListing(null)}
                    container={container}
                />
            ) : null,
        [openedListing, lang, container],
    );

    return (
        <>
            {markers}
            {markerDetails}
        </>
    );
};

export default Markers;
